(function (Drupal, once) {
  'use strict';
  /**
 * Handles the grid/list presentation toggle for the News view only.
 * The catalogue toggle is handled separately by catalog-listview.js.
 */

  const STORAGE_KEY = 'adocs.news.viewMode';

  const MODE_GRID = 'grid';
  const MODE_LIST = 'list';

  const BODY_GRID_CLASS = 'view-mode-grid';
  const BODY_LIST_CLASS = 'view-mode-list';

  /**
   * Returns a valid stored view mode.
   *
   * @return {string|null}
   *   The stored mode or null.
   */
  function getStoredMode() {
    try {
      const mode = window.localStorage.getItem(STORAGE_KEY);

      return mode === MODE_GRID || mode === MODE_LIST
        ? mode
        : null;
    }
    catch (error) {
      return null;
    }
  }

  /**
   * Stores the selected view mode.
   *
   * @param {string} mode
   *   The active view mode.
   */
  function storeMode(mode) {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    }
    catch (error) {
      // Storage may be unavailable or disabled.
    }
  }

  /**
   * Updates the body classes.
   *
   * @param {string} mode
   *   The active view mode.
   */
  function updateBodyState(mode) {
    document.body.classList.toggle(
      BODY_GRID_CLASS,
      mode === MODE_GRID
    );

    document.body.classList.toggle(
      BODY_LIST_CLASS,
      mode === MODE_LIST
    );

    // The head bootstrap keeps the News layout stable before Drupal behaviors
    // run. The body classes above take ownership without an unstyled frame.
    document.documentElement.classList.remove(
      'news-view-mode-grid',
      'news-view-mode-list'
    );
  }

  /**
   * Updates the icon.
   *
   * The icon represents the view the user can switch to.
   *
   * @param {HTMLElement} toggle
   *   The toggle button.
   * @param {string} mode
   *   The active view mode.
   */
  function updateIcon(toggle, mode) {
    const use = toggle.querySelector('use');

    if (!use) {
      return;
    }

    const icon = mode === MODE_GRID
      ? toggle.dataset.iconList
      : toggle.dataset.iconGrid;

    if (icon) {
      use.setAttribute('href', `#${icon}`);
    }

    const svg = use.closest('svg');

    if (!svg) {
      return;
    }

    svg.classList.toggle(
      'picto--list',
      mode === MODE_GRID
    );

    svg.classList.toggle(
      'picto--grid',
      mode === MODE_LIST
    );
  }

  /**
   * Updates the accessible label.
   *
   * @param {HTMLElement} toggle
   *   The toggle button.
   * @param {string} mode
   *   The active view mode.
   */
  function updateLabel(toggle, mode) {
    const label = mode === MODE_GRID
      ? toggle.dataset.labelList
      : toggle.dataset.labelGrid;

    if (!label) {
      return;
    }

    toggle.setAttribute('aria-label', label);

    const hiddenLabel = toggle.querySelector(
      '.visually-hidden'
    );

    if (hiddenLabel) {
      hiddenLabel.textContent = label;
    }
  }

  /**
   * Applies the selected view mode.
   *
   * @param {HTMLElement} toggle
   *   The toggle button.
   * @param {string} mode
   *   The active view mode.
   * @param {boolean} persist
   *   Whether the mode should be persisted.
   */
  function applyMode(toggle, mode, persist = true) {
    toggle.dataset.viewMode = mode;

    updateBodyState(mode);
    updateIcon(toggle, mode);
    updateLabel(toggle, mode);

    if (persist) {
      storeMode(mode);
    }
  }

  /**
   * Returns the opposite view mode.
   *
   * @param {string} mode
   *   The current view mode.
   *
   * @return {string}
   *   The opposite view mode.
   */
  function getNextMode(mode) {
    return mode === MODE_LIST
      ? MODE_GRID
      : MODE_LIST;
  }

  /**
   * Initializes a presentation toggle.
   *
   * @param {HTMLElement} toggle
   *   The toggle button.
   */
  function initializeViewToggle(toggle) {
    const initialMode =
      Drupal.adocsNewsReturn?.getInitialState()?.viewMode
      || getStoredMode()
      || toggle.dataset.viewMode
      || MODE_GRID;

    applyMode(toggle, initialMode, false);

    toggle.addEventListener('click', () => {
      const currentMode =
        toggle.dataset.viewMode || MODE_GRID;

      applyMode(
        toggle,
        getNextMode(currentMode)
      );
    });
  }

  Drupal.behaviors.viewToggle = {
    attach(context) {
      once(
        'view-toggle',
        'button[data-view-toggle]',
        context
      ).forEach(initializeViewToggle);
    },
  };

})(Drupal, once);
